/**
 * 视频节点：原生 video 播放、下载与收藏。
 *
 * 空节点（无视频且非生成中/失败）提供两个入口：本地上传 / 从素材库选择（收藏视频 + 生成视频）。
 * 有视频时可替换、收藏到素材库或下载。
 */
import { Download, Loader2, AlertTriangle, Upload, Bookmark, Layers, Scissors, Maximize2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { CanvasNode, VideoNodeData } from '../types';
import { NodeWrapper } from './NodeWrapper';
import { NodeToolbarButton, NodeToolbarDivider } from './NodeToolbar';
import { isGeneratingStatus } from '../utils/jobStatus';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { boardActions, getBoardState } from '../state/boardStore';
import { uploadVideoFile } from '../utils/uploadVideo';
import { toPersistableMediaUrl } from '../utils/persistableMediaUrl';
import { parseAspectRatioValue } from '../utils/aspectRatio';
import { assetStorage } from '../assetStorage';
import { AssetPickerModal } from '../AssetPickerModal';
import { VideoClipEditor } from './VideoClipEditor';
import { TextEditorDialog } from '../components/dialogs/TextEditorDialog';
import toast from '../../../components/Toast';

interface VideoNodeProps {
  node: CanvasNode<VideoNodeData>;
  selected: boolean;
  highlighted: boolean;
}

export function VideoNode({ node, selected, highlighted }: VideoNodeProps) {
  const data = node.data;
  const url = data.videoUrl ? resolveBrowserMediaUrl(data.videoUrl) : null;
  const [uploading, setUploading] = useState(false);
  // 视频真实宽高比：加载 metadata 后据此设定容器比例，
  // 避免"生成中占位框"与"有视频后画面"因为比例不符而大小观感不一致
  const [videoRatio, setVideoRatio] = useState<number | null>(null);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [promptReviewOpen, setPromptReviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // 替换视频 / 换了一轮生成结果后，旧的宽高比不再适用，重置后由新视频重新上报
  useEffect(() => {
    setVideoRatio(null);
  }, [data.videoUrl]);

  const handleDownload = async () => {
    if (!url) return;
    // fetch 转 blob 下载：跨域 mp4 的 a[download] 会被浏览器忽略变成新开页
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `canvas-video-${node.id.slice(0, 8)}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  /** 本地文件上传后覆盖本节点（空节点即首次填充） */
  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const up = await uploadVideoFile(file);
      boardActions.updateNodeData(node.id, {
        jobId: null,
        videoUrl: up.url,
        coverUrl: null,
        status: 'completed',
        videoDurationSec: up.durationSec,
      });
      toast.success('视频已上传');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '视频上传失败');
    } finally {
      setUploading(false);
    }
  };

  /** 收藏到素材库：blob 地址是会话级的，收藏无意义直接拒绝 */
  const handleCollect = () => {
    const content = toPersistableMediaUrl(data.videoUrl) ?? data.videoUrl;
    if (!content || content.startsWith('blob:')) {
      toast.error('收藏失败：视频地址无效');
      return;
    }
    const result = assetStorage.add({
      type: 'video',
      content,
      coverUrl: toPersistableMediaUrl(data.coverUrl) ?? data.coverUrl ?? null,
      durationSec: data.videoDurationSec ?? null,
      prompt: data.prompt ?? undefined,
      projectId: getBoardState().projectId,
    });
    toast.success(result.created ? '已收藏到素材库' : '该素材已在素材库中');
  };

  return (
    <NodeWrapper
      node={node}
      selected={selected}
      highlighted={highlighted}
      toolbar={
        url && data.status === 'completed' ? (
          <>
            <NodeToolbarButton onClick={() => setEditing(true)} title="剪辑">
              <Scissors className="h-3.5 w-3.5" />
            </NodeToolbarButton>
            <NodeToolbarButton onClick={() => fileInputRef.current?.click()} disabled={uploading} title="替换视频">
              <Upload className="h-3.5 w-3.5" />
            </NodeToolbarButton>
            <NodeToolbarButton onClick={handleCollect} title="收藏到素材库">
              <Bookmark className="h-3.5 w-3.5" />
            </NodeToolbarButton>
            <NodeToolbarDivider />
            <NodeToolbarButton onClick={handleDownload} title="下载视频">
              <Download className="h-3.5 w-3.5" />
            </NodeToolbarButton>
          </>
        ) : null
      }
    >
      <div
        className="w-full overflow-hidden rounded-lg bg-black"
        // 所有状态统一按比例绘制占位框：有视频用真实比例；未加载完 / 无媒体
        //（空节点、生成中、失败）用上游 generate_video 的目标比例（缺省 16:9），
        // 空态、生成态、完成态的占位尺寸完全一致，卡片高度由 ResizeObserver 吸附
        style={{ aspectRatio: videoRatio ?? parseAspectRatioValue(data.aspectRatio, 16 / 9) }}
      >
        {/* 非终态一律视为生成中（兼容历史快照中的 submitting 等未知中间态），防止掉进空节点分支 */}
        {isGeneratingStatus(data.status) ? (
          <div className="flex h-full w-full flex-col items-center justify-center text-white/80">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="mt-2 text-xs">视频生成中...</span>
          </div>
        ) : data.status === 'failed' ? (
          <div className="flex h-full w-full flex-col items-center justify-center bg-red-50 px-3 py-4 text-center text-red-500">
            <AlertTriangle className="h-6 w-6" />
            <span className="mt-2 text-xs font-medium">生成失败</span>
            {data.errorMessage && (
              <span className="mt-1 line-clamp-2 text-[11px] text-red-400/80">{data.errorMessage}</span>
            )}
          </div>
        ) : url ? (
          <video
            src={url}
            poster={data.coverUrl ? resolveBrowserMediaUrl(data.coverUrl) : undefined}
            controls
            className="h-full w-full object-contain"
            onLoadedMetadata={(e) => {
              const el = e.currentTarget;
              if (el.videoWidth > 0 && el.videoHeight > 0) {
                setVideoRatio(el.videoWidth / el.videoHeight);
              }
            }}
          />
        ) : (
          /* 空视频节点：本地上传 / 从素材库选择（保留节点位置与连线） */
          <div className="flex h-full w-full flex-col items-stretch justify-center gap-1.5 px-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex flex-1 flex-col items-center justify-center gap-1 rounded-lg text-white/60 transition hover:bg-white/5 hover:text-white/90 disabled:opacity-60"
            >
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
              <span className="text-xs">{uploading ? '视频上传中...' : '空视频节点 · 点击上传'}</span>
            </button>
            <button
              type="button"
              onClick={() => setAssetPickerOpen(true)}
              disabled={uploading}
              className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-white/25 py-1.5 text-xs text-white/70 transition hover:border-blue-300 hover:bg-white/10 hover:text-white disabled:opacity-60"
            >
              <Layers className="h-3.5 w-3.5" />
              从素材选择
            </button>
          </div>
        )}
      </div>
      {data.modelLabel && (
        <p className="mt-2 flex items-center gap-1 text-[10px] text-slate-400">
          <span className="truncate">{data.modelLabel}</span>
        </p>
      )}
      {data.prompt && (
        <div className="mt-1 flex items-start gap-1">
          <p
            className="line-clamp-2 cursor-pointer text-xs text-light-muted transition hover:text-slate-600"
            onClick={() => setPromptReviewOpen(true)}
            title="点击放大查看"
          >
            {data.prompt}
          </p>
          <button
            type="button"
            onClick={() => setPromptReviewOpen(true)}
            className="mt-0.5 shrink-0 rounded p-0.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            title="放大查看"
          >
            <Maximize2 className="h-3 w-3" />
          </button>
        </div>
      )}
      {/* 上传 / 替换视频的隐藏文件选择器 */}
      <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={onFileChosen} />
      {assetPickerOpen && (
        <AssetPickerModal
          open={assetPickerOpen}
          assetFilter="video"
          onClose={() => setAssetPickerOpen(false)}
          onPick={(asset) => {
            // 回填本节点（保留位置与连线）；清洗收集时可能残留的旧令牌
            boardActions.updateNodeData(node.id, {
              jobId: null,
              videoUrl: toPersistableMediaUrl(asset.content) ?? asset.content,
              coverUrl: toPersistableMediaUrl(asset.coverUrl) ?? asset.coverUrl ?? null,
              status: 'completed',
              prompt: asset.prompt ?? data.prompt,
              videoDurationSec: asset.durationSec ?? null,
            });
          }}
        />
      )}
      {/* 视频剪辑弹窗 */}
      {editing && url && (
        <VideoClipEditor
          sourceNode={node}
          videoUrl={url}
          durationSec={data.videoDurationSec ?? null}
          onClose={() => setEditing(false)}
        />
      )}
      {/* 提示词放大审阅弹窗（只读） */}
      {promptReviewOpen && data.prompt && (
        <TextEditorDialog
          title="提示词"
          initialValue={data.prompt}
          readOnly
          onClose={() => setPromptReviewOpen(false)}
        />
      )}
    </NodeWrapper>
  );
}
